/**
 * /w/{tenantSlug} parent layout — owns the sidebar + topbar +
 * ModuleNavProvider + DPDP modal, and resolves the tenant context
 * from the URL slug.
 *
 * Slug-prefix URL refactor (2026-06-03) entrypoint. Replaces the old
 * _dashboard.tsx's body (the auth gate moved up to _app.tsx).
 *
 * Tenant-slug resolution rules:
 *  - URL slug matches /me.tenant.slug → no-op, render the chrome
 *  - URL slug differs from /me.tenant.slug AND the slug is one of the
 *    caller's memberships → trigger /auth/activate to switch, then
 *    re-render
 *  - URL slug is NOT one of the caller's memberships → bounce to
 *    /select-workspace (the backend will 403 every API call anyway;
 *    this is the UX equivalent)
 *
 * The slug is intentionally cosmetic from the backend's perspective —
 * every API call is authenticated by the cookie, and the cookie binds
 * to one tenant at a time. The URL slug only tells the frontend which
 * workspace the user *wanted* to be in; if it doesn't match the
 * cookie's tenant, we either switch (activate) or bounce.
 */

import { useEffect, useState } from "react";
import {
  Link,
  Outlet,
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isTotpEnrollmentRequired, toApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { BrandLogo } from "@/components/shared/brand-logo";
import { InlineStripFallback, ModuleNavProvider } from "@/components/shared/module-nav";
import { SidebarNav } from "@/components/dashboard/sidebar-nav";
import { SurfaceSwitch } from "@/components/dashboard/surface-switch";
import { TopbarModuleNav } from "@/components/dashboard/topbar-module-nav";
import { SidebarFooter } from "@/components/dashboard/sidebar-footer";
import { InboxButton } from "@/components/dashboard/inbox-button";
import { PhoneBottomBar } from "@/components/dashboard/phone-bottom-bar";
import { InboxProvider } from "@/hooks/use-inbox-count";
import { UserMenu } from "@/components/dashboard/user-menu";
import { NotificationBell } from "@/components/dashboard/notification-bell";
import { DPDPConsentModal } from "@/components/dashboard/dpdp-consent-modal";
import { EntitySwitcher } from "@/components/shared/entity-switcher";
import { StepUpPrompt } from "@/components/auth/step-up-prompt";
import { CommandPalette, openCommandPalette } from "@/components/shared/command-palette";
import { EmployeeLimitDialog } from "@/components/billing/employee-limit-dialog";
import { OverLimitBanner } from "@/components/people/seat-meter";
import { AlumniShell } from "@/components/offboarding/alumni-shell";

function WorkspaceNotFound() {
  return (
    <div className="mx-auto max-w-md py-24 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">
        Page Not Found
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This page doesn&apos;t exist in this workspace.
      </p>
    </div>
  );
}

export const Route = createFileRoute("/_authgated/app/$tenantSlug")({
  component: WorkspaceLayout,
  notFoundComponent: WorkspaceNotFound,
});

/**
 * Phones and tablets (below lg): the same palette from an icon-only 44px
 * button beside the bell — the labelled Search button is desktop-only and
 * a touch screen has no ⌘K.
 */
function PhoneSearchButton() {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={openCommandPalette}
      aria-label="Search"
      className="lg:hidden"
      data-testid="topbar-search-phone"
    >
      <Search aria-hidden className="h-5 w-5" />
    </Button>
  );
}

/** Top-bar Search: opens the ⌘K command palette. */
function SearchButton() {
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <Button
      type="button"
      variant="outline"
      onClick={openCommandPalette}
      className="h-9 gap-2 px-3 text-muted-foreground"
      aria-keyshortcuts={mac ? "Meta+K" : "Control+K"}
      data-testid="topbar-search"
    >
      <Search aria-hidden className="h-4 w-4" />
      <span>Search</span>
      <kbd className="hidden rounded border border-border bg-muted px-1.5 font-sans text-xs text-muted-foreground xl:inline">
        {mac ? "⌘K" : "Ctrl K"}
      </kbd>
    </Button>
  );
}

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-7 w-7 animate-spin text-primary" />
    </div>
  );
}

function WorkspaceLayout() {
  const { user, activate } = useAuth();
  const navigate = useNavigate();
  const { tenantSlug = "" } = useParams({ strict: false }) as {
    tenantSlug?: string;
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activating, setActivating] = useState(false);

  // Resolve URL slug vs the active membership. The _app.tsx parent has
  // already gated unauthenticated + identity-only callers, so user is
  // guaranteed to be a member of SOME tenant by the time we render.
  useEffect(() => {
    if (!user || !tenantSlug) return;
    const activeSlug = user.tenant?.slug;
    if (activeSlug === tenantSlug) return;
    // The URL points at a different workspace than the cookie holds.
    // Try /auth/activate — succeeds if the caller is a member of the
    // URL's workspace, 403s otherwise. activate() rotates the cookie
    // AND calls refresh() under the hood, so /me will reflect the new
    // tenant on the next render.
    let cancelled = false;
    (async () => {
      setActivating(true);
      try {
        await activate(tenantSlug);
      } catch (err) {
        if (cancelled) return;
        // M-1 mandatory-2FA branch — mirrors company-switcher's pattern
        // for parity. The backend has rotated the cookie to enrolment-
        // scoped identity-only at this point, so the only correct move
        // is to force enrolment. Without this branch the user would be
        // silently bounced to /select-workspace with a now-stale `user`
        // state (still tenant-A) while the cookie holds nothing.
        if (isTotpEnrollmentRequired(err)) {
          void navigate({
            to: "/2fa-setup",
            search: { required: 1, next: `/app/${tenantSlug}` },
            replace: true,
          });
          return;
        }
        // Distinguishable error (account_inactive, network 5xx, etc.) —
        // surface the message so the user knows WHY they were bounced.
        // For a plain not_a_member this is a generic toast and the
        // picker explains the rest.
        toast.error(toApiError(err).message);
        void navigate({ to: "/select-workspace", replace: true });
      } finally {
        if (!cancelled) setActivating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantSlug, user, activate, navigate]);

  // Either /me hasn't returned yet OR we're mid-activate. Either way,
  // hold the chrome until the slug matches — otherwise the sidebar /
  // topbar would render the OLD workspace's items while the new
  // activate is in flight.
  if (!user || activating || user.tenant?.slug !== tenantSlug) {
    return <FullScreenSpinner />;
  }

  // A leaver past their last working day: only their own self-service
  // pages answer (backend LeaverAccessGate) — render the minimal alumni
  // shell, never the normal chrome whose widgets would fire 403s.
  if (user.access_restricted) {
    return <AlumniShell tenantSlug={tenantSlug} />;
  }

  return (
    <ModuleNavProvider>
      <InboxProvider>
        {/* --bottom-bar-h: the phone bottom bar's height (0 from lg up).
            Anything pinned to the bottom of the screen sits above it with
            `bottom-[var(--bottom-bar-h,0px)]` (guarded by
            lib/bottom-pinned-guard.test.ts). */}
        <div className="flex min-h-screen bg-gray-50 [--bottom-bar-h:calc(3.5rem_+_1px_+_env(safe-area-inset-bottom))] lg:[--bottom-bar-h:0px]">
          {/* Skip-to-content link (a11y) — first focusable element. On
              tab, it appears in the top-left of the viewport with a high-
              contrast pill; activating it jumps focus past the ~25-link
              sidebar straight to <main id="main-content">. */}
          <a
            href="#main-content"
            className="sr-only z-50 focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Skip to content
          </a>
          {/* Desktop rail: logo, the Me | Manage switch (pinned), the side's
              items (scroll), then the pinned footer (Settings + Help). */}
          <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-border bg-background lg:flex">
            <div className="flex h-16 shrink-0 items-center border-b border-border px-5">
              <Link
                to="/app/$tenantSlug"
                params={{ tenantSlug }}
                aria-label="WorkRight home"
                // Exact: the router's prefix match made the brand link
                // aria-current="page" on every page of the workspace.
                activeOptions={{ exact: true }}
              >
                <BrandLogo />
              </Link>
            </div>
            <SurfaceSwitch className="shrink-0 px-3 pt-3" />
            <SidebarNav />
            <SidebarFooter />
          </aside>

          {/* Phone drawer — opened by Menu in the bottom bar: the switch (only
              with Manage; no switch, no space for it), the full sidebar, then Settings and Help. */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent
              side="left"
              className="flex w-72 flex-col gap-0 p-0"
              // Start on the current page's item (or the side shown), not
              // the first link of the list or Help at the bottom.
              onOpenAutoFocus={(e) => {
                if (!(e.target instanceof HTMLElement)) return;
                e.preventDefault();
                const here =
                  e.target.querySelector<HTMLElement>('[aria-current="page"]') ??
                  e.target.querySelector<HTMLElement>('[aria-current="true"]') ??
                  e.target;
                here.focus({ preventScroll: true });
              }}
            >
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex h-16 shrink-0 items-center border-b border-border px-5">
                <BrandLogo />
              </div>
              <SurfaceSwitch
                className="shrink-0 px-3 pt-3"
                onNavigate={() => setMobileOpen(false)}
                showListedSide
              />
              <SidebarNav onNavigate={() => setMobileOpen(false)} />
              <SidebarFooter onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          {/* Main column */}
          <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
            {/* Top bar. Desktop: module tabs or a breadcrumb on the left;
                Search · Inbox (n) · Notifications · Account on the right.
                Phone: logo, then notifications and account (the bottom bar
                carries the primary navigation). */}
            <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:px-6">
              <Link
                to="/app/$tenantSlug"
                params={{ tenantSlug }}
                aria-label="WorkRight home"
                activeOptions={{ exact: true }}
                // 44px tap area around the 32px mark on touch (docs/71 §63).
                className="inline-flex items-center justify-center pointer-coarse:min-h-11 pointer-coarse:min-w-11 lg:hidden"
              >
                <BrandLogo iconOnly />
              </Link>

              <TopbarModuleNav />

              <div className="ml-auto flex items-center gap-1 sm:gap-2">
                <div className="hidden items-center gap-2 lg:flex">
                  <SearchButton />
                  <InboxButton />
                </div>
                <EntitySwitcher />
                <PhoneSearchButton />
                <NotificationBell />
                <UserMenu />
              </div>
            </header>

            {/* overflow-x-clip + min-w-0: a stray too-wide child can't widen
                the page (and with it the layout viewport that dialogs and
                the bottom bar are placed against). clip, unlike hidden,
                makes no scroll box, so sticky bars keep working; wide
                tables and strips scroll inside their own boxes. */}
            <main
              id="main-content"
              className="min-w-0 flex-1 overflow-x-clip scroll-pb-24 px-4 pb-24 pt-6 sm:px-6 lg:px-8 lg:pb-6"
            >
              <OverLimitBanner />
              <InlineStripFallback />
              <Outlet />
            </main>
          </div>

          <PhoneBottomBar menuOpen={mobileOpen} onMenu={() => setMobileOpen(true)} />

          {/* DPDP Act 2023 per-membership employee-consent capture */}
          <DPDPConsentModal />

          {/* Cmd-K / Ctrl-K command palette (docs/52 W2-10). Mounted once
              here so the global keydown listener + palette are live on every
              /app/$tenantSlug/* route; it manages its own open state. */}
          <CommandPalette />

          {/* 402 employee_limit_reached → upgrade dialog (lib/api/client.ts) */}
          <EmployeeLimitDialog />

          {/* 401 totp_required → ask for the 2FA code in place and replay the
              action (lib/api/step-up.ts; QA payroll #11). */}
          <StepUpPrompt />

        </div>
      </InboxProvider>
    </ModuleNavProvider>
  );
}
